import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, KeyRound, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
    const { user, login } = useAuth();
    const [email, setEmail] = useState("admin@hotelerp.com");
    const [password, setPassword] = useState("admin123");
    const [loading, setLoading] = useState(false);

    if (user && user.email) return <Navigate to="/" replace />;

    const onSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        const res = await login(email, password);
        setLoading(false);
        if (!res.ok) {
            toast.error(res.error || "Login failed");
        } else {
            toast.success("Welcome back");
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50">
            {/* Left: form */}
            <div className="flex items-center justify-center px-8 py-12">
                <div className="w-full max-w-md">
                    <div className="mb-10 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-sm bg-[#0A1C3E] flex items-center justify-center">
                            <span className="text-[#C5B08A] font-bold font-display">M</span>
                        </div>
                        <div>
                            <div className="font-display font-bold text-slate-900 tracking-tight">MERIDIAN</div>
                            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Hotel ERP · PMS</div>
                        </div>
                    </div>

                    <h1 className="text-3xl font-display font-bold tracking-tight text-slate-900" data-testid="login-title">
                        Welcome back
                    </h1>
                    <p className="text-sm text-slate-500 mt-2">
                        Sign in to your enterprise hospitality workspace.
                    </p>

                    <form onSubmit={onSubmit} className="mt-8 space-y-5">
                        <div>
                            <Label className="text-xs font-medium text-slate-700 uppercase tracking-wider">Email</Label>
                            <Input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="mt-1.5"
                                required
                                data-testid="login-email"
                            />
                        </div>
                        <div>
                            <div className="flex justify-between items-center">
                                <Label className="text-xs font-medium text-slate-700 uppercase tracking-wider">Password</Label>
                                <a className="text-xs text-slate-500 hover:text-[#0A1C3E]">Forgot?</a>
                            </div>
                            <Input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="mt-1.5"
                                required
                                data-testid="login-password"
                            />
                        </div>

                        <Button
                            type="submit"
                            disabled={loading}
                            className="w-full h-11 bg-[#0A1C3E] hover:bg-[#132D5C] text-white rounded-sm font-medium"
                            data-testid="login-submit"
                        >
                            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
                        </Button>
                    </form>

                    <div className="mt-8 p-4 bg-white border border-slate-200 rounded-md">
                        <div className="flex items-center gap-2 text-xs font-semibold text-slate-700 mb-2">
                            <KeyRound className="w-3.5 h-3.5" /> Demo Credentials
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-slate-600 font-mono">
                            <span>admin@hotelerp.com</span><span className="text-slate-400">admin123</span>
                            <span>manager@hotelerp.com</span><span className="text-slate-400">demo123</span>
                            <span>frontdesk@hotelerp.com</span><span className="text-slate-400">demo123</span>
                            <span>housekeeping@hotelerp.com</span><span className="text-slate-400">demo123</span>
                            <span>restaurant@hotelerp.com</span><span className="text-slate-400">demo123</span>
                            <span>hr@hotelerp.com</span><span className="text-slate-400">demo123</span>
                        </div>
                    </div>

                    <div className="mt-6 flex items-center gap-2 text-xs text-slate-500">
                        <ShieldCheck className="w-3.5 h-3.5" />
                        JWT · Role-Based Access · Multi-Property
                    </div>
                </div>
            </div>

            {/* Right: image */}
            <div className="hidden lg:block relative overflow-hidden bg-[#0A1C3E]">
                <img
                    src="https://images.unsplash.com/photo-1723465308831-29da05e011f3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBob3RlbCUyMGV4dGVyaW9yfGVufDB8fHx8MTc4MzE1NTc3OXww&ixlib=rb-4.1.0&q=85"
                    alt="Grand Meridian Palace"
                    className="absolute inset-0 w-full h-full object-cover opacity-60"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A1C3E] via-[#0A1C3E]/60 to-transparent" />
                <div className="relative z-10 h-full flex flex-col justify-end p-12">
                    <div className="text-[#C5B08A] text-xs font-bold tracking-[0.3em] uppercase mb-3">Grand Meridian Palace</div>
                    <div className="text-white font-display text-4xl font-bold tracking-tight max-w-md leading-tight">
                        Running the world&apos;s finest hotels, one guest at a time.
                    </div>
                    <div className="text-white/70 text-sm mt-3 max-w-md">
                        Unified property management, POS, housekeeping, purchasing, HR and finance — for hotel chains and independent properties alike.
                    </div>
                </div>
            </div>
        </div>
    );
}
